// ─── Domain models ────────────────────────────────────────────────────────────

export interface MonthlyPayment {
  month: number;
  calendarYear: number;
  calendarMonth: number; // 0-indexed
  emi: number;
  principal: number;
  interest: number;
  prepayment: number;
  balance: number;
  totalPrincipalPaid: number;
  totalInterestPaid: number;
}

export interface YearlyPayment {
  calendarYear: number;
  emi: number;
  principal: number;
  interest: number;
  prepayment: number;
  balance: number;
}

export interface Prepayment {
  id: number;
  month: number;
  amount: number;
}

export interface LoanSummary {
  emi: number;
  totalInterest: number;
  totalPayment: number;
  totalMonths: number;
}

export interface ScheduleResult {
  months: MonthlyPayment[];
  totalInterest: number;
  totalPayment: number;
  finalEMI: number;
}

export interface AiInsight {
  type: 'success' | 'warning' | 'info' | 'tip';
  icon: string;
  title: string;
  message: string;
  actionLabel?: string;
  actionKey?: 'setTenure' | 'incrementTenure' | 'setRate' | 'setLoanAmount' | 'addPrepayment';
  actionValue?: any;
}

export interface InsightParams {
  emi: number;
  loanTermYears: number;
  annualInterestRate: number;
  loanAmount: number;
  totalInterest: number;
  hasPrepayments: boolean;
  borrowerAge: number;
  monthlyIncome: number;
  baseTotalMonths: number;
  startDate: string;
  prepaymentOption: 'reduce_tenure' | 'reduce_emi';
}

export interface ChartRenderData {
  loanAmount: number;
  finalSummary: LoanSummary;
  yearlySchedule: YearlyPayment[];
  hasPrepayments: boolean;
  monthlySchedule: MonthlyPayment[];
  baseMonths: number;
  baseYearly: YearlyPayment[];
}

// ─── Validation ───────────────────────────────────────────────────────────────

export interface FieldError {
  field: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: Record<string, string>;
}

export interface PrepaymentFieldError {
  month?: string;
  amount?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export interface LoanConstraints {
  amount:       { min: number; max: number; step: number };
  rate:         { min: number; max: number; step: number };
  term:         { min: number; max: number; step: number };
  age:          { min: number; max: number; step: number };
  income:       { min: number; max: number; step: number };
  prepayMonth:  { min: number };
  prepayAmount: { min: number; max: number };
}

export const LOAN_CONSTRAINTS: LoanConstraints = {
  amount:   { min: 100_000,  max: 100_000_000, step: 50_000  },
  rate:     { min: 1,        max: 30,          step: 0.01    },
  term:     { min: 1,        max: 30,          step: 1       },
  age:      { min: 18,       max: 70,          step: 1       },
  income:   { min: 0,        max: 100_000_000, step: 1000    },
  prepayMonth:  { min: 1 },
  prepayAmount: { min: 1000, max: 10_000_000 }
} as const;

export const PERSONAL_LOAN_CONSTRAINTS: LoanConstraints = {
  amount:   { min: 25_000,   max: 4_000_000,   step: 5_000   },
  rate:     { min: 9,        max: 24,          step: 0.01    },
  term:     { min: 1,        max: 7,           step: 1       },
  age:      { min: 18,       max: 70,          step: 1       },
  income:   { min: 0,        max: 100_000_000, step: 1000    },
  prepayMonth:  { min: 1 },
  prepayAmount: { min: 1000, max: 4_000_000 }
} as const;

export const CAR_LOAN_CONSTRAINTS: LoanConstraints = {
  amount:   { min: 50_000,   max: 20_000_000,  step: 10_000  },
  rate:     { min: 7,        max: 15,          step: 0.01    },
  term:     { min: 1,        max: 8,           step: 1       },
  age:      { min: 18,       max: 70,          step: 1       },
  income:   { min: 0,        max: 100_000_000, step: 1000    },
  prepayMonth:  { min: 1 },
  prepayAmount: { min: 1000, max: 10_000_000 }
} as const;
